import { Button } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import Decimal from "decimal.js";
import { useTranslation } from "react-i18next";
import type { CurrencyPrimitive } from "@/api/cashier";
import { InputFieldBase } from "@/components/forms/input-field-base/InputFieldBase";
import { LastUpdatedCounter } from "@/components/last-updated-counter/LastUpdatedCounter";
import { useAmountInput } from "@/hooks/useAmountInput";
import styles from "./WithdrawAmountInput.module.css";
import refundStyles from "./WithdrawRefundAmountInput.module.css";

type WithdrawRefundAmountInputProps = {
  id?: string;
  /** Emitted on every keystroke. amount=undefined when empty/invalid. */
  onChange: (amount: Decimal | undefined) => void;
  onError?: (
    error: "format" | "min" | "max" | "",
    input: string | undefined
  ) => void;
  currency: CurrencyPrimitive;
  disabled?: boolean;
  min?: Decimal;
  max?: Decimal;
  limits?: string;
  style?: any;
  trading?: {
    currency: CurrencyPrimitive;
    amount: Decimal;
  };
  tradingValue?: Decimal | null;
  validationError?: string;
  locale: string;
  /** Live exchange rate: how many trading-currency units equal 1 deposit-currency unit (e.g. 1.17 for "1 EUR = 1.17 USD") */
  exchangeRate?: number;
  /** Display unit for the rate line — almost always 1 */
  exchangeRateUnit?: number;
  lastUpdated?: number;
  nextUpdate?: number;
};

export const WithdrawRefundAmountInput = ({
  id,
  min,
  max,
  onChange,
  onError,
  disabled,
  currency,
  limits,
  style,
  trading,
  tradingValue,
  locale,
  exchangeRate,
  exchangeRateUnit,
  lastUpdated,
  nextUpdate,
}: WithdrawRefundAmountInputProps) => {
  const { t } = useTranslation();

  const { value, error, handleChange, setFromDecimal } = useAmountInput({
    currency,
    min,
    max,
    maxDecimalPlaces: 2,
    onChange,
    onError,
    locale,
  });

  const isMaxSet = typeof max !== "undefined";
  const isMinSet = typeof min !== "undefined";
  const invalidLimits = isMaxSet && isMinSet && max!.lt(min!);

  const formattedMin = min?.toNumber().toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: true,
  });

  const formattedMax = max?.toNumber().toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: true,
  });

  const placeholder = invalidLimits
    ? "0"
    : isMaxSet && isMinSet
      ? `${formattedMin}-${formattedMax}`
      : isMaxSet
        ? `0-${formattedMax}`
        : "0";

  const effectiveMax = max ?? new Decimal(Number.MAX_SAFE_INTEGER);

  const showTradingInline =
    trading &&
    trading.currency.code !== currency?.code &&
    tradingValue !== undefined;

  const availableText =
    limits ??
    t("withdrawals.methods.amount_input.available_balance", {
      amount: !isMaxSet
        ? "--"
        : `${formattedMax} ${currency?.code}` +
          (!showTradingInline && trading?.currency?.code !== currency?.code
            ? ` (≈${trading?.amount.toNumber().toLocaleString(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
                useGrouping: true,
              })} ${trading?.currency?.code})`
            : ""),
    });

  min = min ?? new Decimal(0);
  max = max ?? new Decimal(Number.MAX_SAFE_INTEGER);

  if (showTradingInline) {
    return (
      <div style={style} className={refundStyles.wrapper}>
        <div className={refundStyles.row}>
          <InputFieldBase
            data-testid="withdraw-amount-input-field-base"
            disabled={disabled}
            size="lg"
            labelFor="name"
            hideErrorPlaceholder
            error={error}
            className={refundStyles.inputSide}
          >
            <input
              id={id}
              data-testid="withdraw-amount-input-field-input"
              min={min.toNumber()}
              max={max.toNumber()}
              type="text"
              inputMode="decimal"
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              placeholder={placeholder}
              disabled={disabled}
            />
            <span className={clsx(disabled && styles.disabledCurrency)}>
              {currency?.code}
            </span>
            {isMaxSet && (
              <Button
                onClick={() => setFromDecimal(effectiveMax)}
                id={`${id}_maxButton`}
                data-testid="withdraw-amount-input-field-max-button"
                disabled={disabled || invalidLimits || effectiveMax.lte(0)}
                className={clsx(styles.maxButton)}
                variant="ps-btn-primary"
                size="ps-btn-sm"
                type="button"
              >
                {t("withdrawals.methods.amount_input.max")}
              </Button>
            )}
          </InputFieldBase>

          <span className={refundStyles.equals}>=</span>

          <div className={refundStyles.tradingBox}>
            <span className={refundStyles.tradingAmount}>
              {tradingValue != null
                ? tradingValue.toNumber().toLocaleString(locale, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: trading!.currency.decimals ?? 2,
                    useGrouping: true,
                  })
                : "—"}
            </span>
            <span className={refundStyles.tradingCurrencyCode}>
              {trading!.currency.code}
            </span>
          </div>
        </div>

        <div className={refundStyles.footer}>
          <span className={refundStyles.available}>{availableText}</span>
          {exchangeRate != null && exchangeRateUnit != null ? (
            <div
              className={`ps-flex ps-gap-2 ps-items-center ${refundStyles.rate}`}
            >
              <span>
                {exchangeRateUnit} {currency?.code} ={" "}
                {exchangeRate.toLocaleString(locale, {
                  minimumFractionDigits: trading!.currency.decimals ?? 2,
                  maximumFractionDigits: trading!.currency.decimals ?? 2,
                  useGrouping: true,
                })}{" "}
                {trading!.currency.code}
              </span>
              {lastUpdated != null && nextUpdate != null && (
                <span className={refundStyles.spinnerScale}>
                  <LastUpdatedCounter
                    hideText
                    lastUpdated={lastUpdated}
                    nextUpdate={nextUpdate}
                  />
                </span>
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <InputFieldBase
      data-testid="withdraw-amount-input-field-base"
      disabled={disabled}
      hint={availableText}
      size="lg"
      labelFor="name"
      style={style}
      error={error}
    >
      <input
        id={id}
        data-testid="withdraw-amount-input-field-input"
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onWheel={(e) => e.currentTarget.blur()}
        placeholder={placeholder}
        disabled={disabled}
      />
      <span className={clsx(disabled && styles.disabledCurrency)}>
        {currency?.code}
      </span>
      {isMaxSet && (
        <Button
          id={`${id}_maxButton`}
          data-testid="withdraw-amount-input-field-max-button"
          disabled={disabled || invalidLimits || effectiveMax.lte(0)}
          className={clsx(styles.maxButton)}
          variant="ps-btn-primary"
          size="ps-btn-sm"
          type="button"
          onClick={() => setFromDecimal(effectiveMax)}
        >
          {t("withdrawals.methods.amount_input.max")}
        </Button>
      )}
    </InputFieldBase>
  );
};
